import { Injectable } from '@angular/core';
import { Probate } from '../Models/Probate';
import { Bequest } from '../Models/Bequest';
import { Executor } from '../Models/Executor';
import { ProbateAddress } from '../Models/ProbateAddress';

export interface FeedbackCandidate {
  fieldPath: string;
  beforeValue: string | null;
  afterValue: string | null;
  suggestedFeedbackTypeId?: number;
}

// FeedbackType ids, per the seeded FeedbackType lookup table
const FEEDBACK_TYPE = {
  MissingPrecondition: 1,
  IncorrectPrecondition: 2,
  MissingWishes: 3,
  IncorrectWishes: 4,
  IncorrectBequestDetails: 5,
  MissingBequest: 6,
  RevokedGift: 7,
  IncorrectExecutorDetails: 8,
  IncorrectBeneficiaryLink: 9,
  IncorrectAssociatedText: 10,
  IncorrectDeceasedDetails: 11
};

interface ScalarField {
  key: keyof Probate;
  label: string;
  isDate?: boolean;
  suggestedFeedbackTypeId?: number;
}

const SCALAR_FIELDS: ScalarField[] = [
  { key: 'deceasedName', label: 'Deceased Name', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'title', label: 'Title', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'afterTitle', label: 'After Title', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'gender', label: 'Gender', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'dateOfDeath', label: 'Date of Death', isDate: true, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'dateOfBirth', label: 'Date of Birth', isDate: true, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'relationshipToRelative', label: 'Relationship to Relative', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'registryName', label: 'Registry' },
  { key: 'dateOfIssue', label: 'Date of Issue', isDate: true },
  { key: 'grossValue', label: 'Gross Value', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'netValue', label: 'Net Value', suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails },
  { key: 'notes', label: 'Notes' },
  { key: 'benhamClause', label: 'Benham Clause' }
];

@Injectable({ providedIn: 'root' })
export class FeedbackTrackingService {
  private snapshot: Probate | null = null;

  get isTracking(): boolean {
    return this.snapshot !== null;
  }

  hasSnapshot(): boolean {
    return this.snapshot !== null;
  }

  startTracking(probate: Probate): void {
    this.snapshot = JSON.parse(JSON.stringify(probate));
  }

  stopTracking(): void {
    this.snapshot = null;
  }

  computeCandidates(current: Probate, formatBequestType: (bequestType: any) => string): FeedbackCandidate[] {
    if (!this.snapshot) return [];
    const before = this.snapshot;
    const candidates: FeedbackCandidate[] = [];

    for (const field of SCALAR_FIELDS) {
      const beforeStr = this.toComparableString((before as any)[field.key], field.isDate);
      const afterStr = this.toComparableString((current as any)[field.key], field.isDate);
      if (beforeStr !== afterStr) {
        candidates.push({
          fieldPath: field.label,
          beforeValue: beforeStr,
          afterValue: afterStr,
          suggestedFeedbackTypeId: field.suggestedFeedbackTypeId
        });
      }
    }

    this.diffAliases(before.aliases, current.aliases, candidates);
    this.diffAddresses(before.addresses, current.addresses, candidates);
    this.diffExecutors(before.executors, current.executors, candidates);
    this.diffPreconditions(before.preconditions, current.preconditions, candidates);
    this.diffBequests(before.bequests, current.bequests, candidates, formatBequestType);

    return candidates;
  }

  private diffAliases(before: string[] | undefined, after: string[] | undefined, candidates: FeedbackCandidate[]): void {
    const beforeSet = new Set((before || []).map(a => (a || '').trim()).filter(a => a.length > 0));
    const afterSet = new Set((after || []).map(a => (a || '').trim()).filter(a => a.length > 0));

    for (const alias of afterSet) {
      if (!beforeSet.has(alias)) {
        candidates.push({ fieldPath: 'Alias Added', beforeValue: null, afterValue: alias });
      }
    }
    for (const alias of beforeSet) {
      if (!afterSet.has(alias)) {
        candidates.push({ fieldPath: 'Alias Removed', beforeValue: alias, afterValue: null });
      }
    }
  }

  private diffAddresses(before: ProbateAddress[] | undefined, after: ProbateAddress[] | undefined, candidates: FeedbackCandidate[]): void {
    const key = (a: ProbateAddress) => a.id > 0 ? `id:${a.id}` : `key:${(a.address || '').trim().toLowerCase()}|${(a.postcode || '').trim().toLowerCase()}`;
    const summary = (a: ProbateAddress) => [a.address, a.town, a.county, a.postcode, a.country].filter(p => p && p.trim().length > 0).join(', ');

    const { added, removed, matched } = this.matchByKey(before || [], after || [], key);

    // Every entry here is one of the deceased's own addresses (current or former - see
    // ProbateAddress.isFinal), never a third party's, so any change is a deceased-detail correction.
    for (const a of added) candidates.push({ fieldPath: 'Address Added', beforeValue: null, afterValue: summary(a), suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails });
    for (const a of removed) candidates.push({ fieldPath: 'Address Removed', beforeValue: summary(a), afterValue: null, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails });
    for (const { before: b, after: a } of matched) {
      const beforeSummary = summary(b);
      const afterSummary = summary(a);
      if (beforeSummary !== afterSummary) {
        candidates.push({ fieldPath: 'Address Changed', beforeValue: beforeSummary, afterValue: afterSummary, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectDeceasedDetails });
      }
    }
  }

  private diffExecutors(before: Executor[] | undefined, after: Executor[] | undefined, candidates: FeedbackCandidate[]): void {
    const key = (e: Executor) => e.id > 0 ? `id:${e.id}` : `name:${(e.name || '').trim().toLowerCase()}`;
    const summary = (e: Executor) => [e.name, e.relationship, e.address, e.town, e.county, e.postcode, e.country].filter(p => p && p.trim().length > 0).join(', ');

    const { added, removed, matched } = this.matchByKey(before || [], after || [], key);

    for (const e of added) candidates.push({ fieldPath: 'Executor Added', beforeValue: null, afterValue: summary(e), suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectExecutorDetails });
    for (const e of removed) candidates.push({ fieldPath: 'Executor Removed', beforeValue: summary(e), afterValue: null, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectExecutorDetails });
    for (const { before: b, after: a } of matched) {
      const beforeSummary = summary(b);
      const afterSummary = summary(a);
      if (beforeSummary !== afterSummary) {
        candidates.push({ fieldPath: 'Executor Changed', beforeValue: beforeSummary, afterValue: afterSummary, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectExecutorDetails });
      }
    }
  }

  private diffPreconditions(before: { text: string }[] | undefined, after: { text: string }[] | undefined, candidates: FeedbackCandidate[]): void {
    const beforeSet = new Set((before || []).map(p => (p.text || '').trim()).filter(t => t.length > 0));
    const afterSet = new Set((after || []).map(p => (p.text || '').trim()).filter(t => t.length > 0));

    for (const text of afterSet) {
      if (!beforeSet.has(text)) {
        candidates.push({ fieldPath: 'Precondition Added', beforeValue: null, afterValue: text, suggestedFeedbackTypeId: FEEDBACK_TYPE.MissingPrecondition });
      }
    }
    for (const text of beforeSet) {
      if (!afterSet.has(text)) {
        candidates.push({ fieldPath: 'Precondition Removed', beforeValue: text, afterValue: null, suggestedFeedbackTypeId: FEEDBACK_TYPE.IncorrectPrecondition });
      }
    }
  }

  private diffBequests(before: Bequest[] | undefined, after: Bequest[] | undefined, candidates: FeedbackCandidate[], formatBequestType: (bequestType: any) => string): void {
    const beforeList = before || [];
    const afterList = after || [];

    const beforeById = new Map(beforeList.filter(b => b.id > 0).map(b => [b.id, b]));
    const afterById = new Map(afterList.filter(b => b.id > 0).map(b => [b.id, b]));

    for (const bequest of afterList) {
      if (bequest.id <= 0) {
        candidates.push({
          fieldPath: 'Bequest Added',
          beforeValue: null,
          afterValue: this.buildBequestSummary(bequest, formatBequestType),
          suggestedFeedbackTypeId: FEEDBACK_TYPE.MissingBequest
        });
      }
    }

    for (const [id, beforeBequest] of beforeById) {
      const afterBequest = afterById.get(id);
      if (!afterBequest) {
        candidates.push({
          fieldPath: 'Bequest Removed',
          beforeValue: this.buildBequestSummary(beforeBequest, formatBequestType),
          afterValue: null,
          suggestedFeedbackTypeId: FEEDBACK_TYPE.RevokedGift
        });
        continue;
      }

      const suggestedFeedbackTypeId = this.classifyBequestChange(beforeBequest, afterBequest);
      if (suggestedFeedbackTypeId !== undefined) {
        candidates.push({
          fieldPath: 'Bequest Changed',
          beforeValue: this.buildBequestSummary(beforeBequest, formatBequestType),
          afterValue: this.buildBequestSummary(afterBequest, formatBequestType),
          suggestedFeedbackTypeId
        });
      }
    }
  }

  // Returns the suggested FeedbackType for whatever changed between the two bequests, or
  // `undefined` if nothing changed at all (the caller then skips this bequest entirely). When
  // several aspects change at once, the most specific/severe reason wins: which charity is linked
  // matters more than a wording tweak, so beneficiaryId is checked first, then the two fields with
  // their own dedicated FeedbackTypes (wishes, associated text), falling back to the general
  // "incorrect bequest details" bucket for value/type/name/category/precondition-link changes.
  private classifyBequestChange(before: Bequest, after: Bequest): number | undefined {
    const norm = (v: any) => (v === undefined || v === null || v === '') ? null : v;
    const numEqual = (a: any, b: any) => {
      const an = norm(a);
      const bn = norm(b);
      if (an === null || bn === null) return an === bn;
      return Number(an) === Number(bn);
    };
    const categoriesEqual = (a: number[] | null | undefined, b: number[] | null | undefined) => {
      const aSorted = [...(a || [])].sort((x, y) => x - y).join(',');
      const bSorted = [...(b || [])].sort((x, y) => x - y).join(',');
      return aSorted === bSorted;
    };

    const beneficiaryLinkChanged = norm(before.beneficiaryId) !== norm(after.beneficiaryId);

    const wishesBefore = norm(before.wishes);
    const wishesAfter = norm(after.wishes);
    const wishesChanged = wishesBefore !== wishesAfter;

    const associatedTextChanged = norm(before.associatedTextOnWill) !== norm(after.associatedTextOnWill);

    const otherDetailsChanged = norm(before.beneficiaryNameOnWill) !== norm(after.beneficiaryNameOnWill)
      || !numEqual(before.cashAmount, after.cashAmount)
      || !numEqual(before.percentageAmount, after.percentageAmount)
      || norm(before.effectDescription) !== norm(after.effectDescription)
      || norm(before.bequestType) !== norm(after.bequestType)
      || !categoriesEqual(before.charityCategoryId, after.charityCategoryId)
      || norm(before.precondition?.text) !== norm(after.precondition?.text);

    if (!beneficiaryLinkChanged && !wishesChanged && !associatedTextChanged && !otherDetailsChanged) {
      return undefined;
    }

    if (beneficiaryLinkChanged) return FEEDBACK_TYPE.IncorrectBeneficiaryLink;
    if (wishesChanged) return wishesBefore === null ? FEEDBACK_TYPE.MissingWishes : FEEDBACK_TYPE.IncorrectWishes;
    if (associatedTextChanged && !otherDetailsChanged) return FEEDBACK_TYPE.IncorrectAssociatedText;
    return FEEDBACK_TYPE.IncorrectBequestDetails;
  }

  private buildBequestSummary(bequest: Bequest, formatBequestType: (bequestType: any) => string): string {
    const name = bequest.organisationName || bequest.beneficiaryNameOnWill || bequest.name || 'Unnamed Beneficiary';
    const notification = (bequest.notificationType || '').toUpperCase() || 'UNKNOWN';
    const type = formatBequestType(bequest.bequestType) || '—';
    let value: string;
    if (bequest.cashAmount !== null && bequest.cashAmount !== undefined) {
      value = `£${Number(bequest.cashAmount).toLocaleString('en-GB')}`;
    } else if (bequest.percentageAmount !== null && bequest.percentageAmount !== undefined) {
      value = `${bequest.percentageAmount}%`;
    } else if (bequest.effectDescription) {
      value = bequest.effectDescription;
    } else {
      value = '—';
    }
    return `${name} - ${notification} - ${type} - ${value}`;
  }

  private matchByKey<T>(before: T[], after: T[], key: (item: T) => string): { added: T[]; removed: T[]; matched: { before: T; after: T }[] } {
    const beforeMap = new Map(before.map(item => [key(item), item]));
    const afterMap = new Map(after.map(item => [key(item), item]));

    const added: T[] = [];
    const removed: T[] = [];
    const matched: { before: T; after: T }[] = [];

    for (const [k, item] of afterMap) {
      const beforeItem = beforeMap.get(k);
      if (beforeItem) {
        matched.push({ before: beforeItem, after: item });
      } else {
        added.push(item);
      }
    }
    for (const [k, item] of beforeMap) {
      if (!afterMap.has(k)) {
        removed.push(item);
      }
    }

    return { added, removed, matched };
  }

  // Normalizes a scalar value (Date / Moment-like / number / string) to a stable string for both
  // comparison and display, so JSON round-tripping the snapshot (Date -> ISO string) or differing
  // Date/Moment representations between the snapshot and the live form don't produce phantom diffs.
  private toComparableString(value: any, isDate?: boolean): string | null {
    if (value === null || value === undefined || value === '') return null;

    if (isDate) {
      let d: Date | null = null;
      if (typeof value === 'object' && typeof value.format === 'function') {
        // Moment-like object
        return value.format('DD/MM/YYYY');
      } else if (value instanceof Date) {
        d = value;
      } else {
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) d = parsed;
      }
      if (!d) return String(value);
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      return `${day}/${month}/${d.getFullYear()}`;
    }

    if (typeof value === 'number') return String(value);

    return String(value).trim() || null;
  }
}
